import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaShieldAlt,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaUserTie,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/transport-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT =
  "linear-gradient(135deg, #7c3aed 0%, #6366f1 55%, #0ea5e9 100%)";

function staffRowKey(staff) {
  return staff?.staff_id != null ? String(staff.staff_id) : null;
}

function isTransportModule(module) {
  const blob = [
    module?.module_name,
    module?.module_activity,
    module?.component_name,
    module?.package_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return blob.includes("transport");
}

function getInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

const TransportPermission = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedStaffId, setExpandedStaffId] = useState("");
  const [privilegeMap, setPrivilegeMap] = useState({});
  const [privilegeLoadingId, setPrivilegeLoadingId] = useState("");
  const [updatingKey, setUpdatingKey] = useState("");

  const loadStaff = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      setStaffList([]);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getStaff.php`, {
        params: { school_code: schoolCode },
      });

      if (res.data?.api_status === "OK" && Array.isArray(res.data?.staff)) {
        setStaffList(res.data.staff);
      } else {
        setStaffList([]);
        myToast.notify_warning(res.data?.api_remark || "Could not load staff list.");
      }
    } catch (error) {
      console.error("[TransportPermission] staff load error", error);
      setStaffList([]);
      myToast.notify_warning("Network error loading staff.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStaffPrivileges = useCallback(async (staffId) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !staffId) return;

    setPrivilegeLoadingId(String(staffId));
    try {
      const res = await axios.get(`${API_BASE()}getStaffPrivilege.php`, {
        params: {
          school_code: schoolCode,
          staff_id: staffId,
        },
      });

      if (res.data?.api_status === "OK" && Array.isArray(res.data?.data)) {
        const transportModules = res.data.data.filter(isTransportModule);
        setPrivilegeMap((prev) => ({
          ...prev,
          [String(staffId)]: transportModules,
        }));
      } else {
        setPrivilegeMap((prev) => ({
          ...prev,
          [String(staffId)]: [],
        }));
        myToast.notify_warning(
          res.data?.api_remark || "Could not load transport permissions."
        );
      }
    } catch (error) {
      console.error("[TransportPermission] privilege load error", error);
      myToast.notify_warning("Network error loading permissions.");
    } finally {
      setPrivilegeLoadingId("");
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const filteredStaff = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return staffList;

    return staffList.filter((staff) => {
      const blob = [
        staff.staff_name,
        staff.staff_role,
        staff.staff_id,
        staff.department_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(query);
    });
  }, [staffList, search]);

  const grantedCount = useMemo(() => {
    return Object.values(privilegeMap).reduce((total, modules) => {
      if (!Array.isArray(modules)) return total;
      return total + modules.filter((row) => row.status === "Granted").length;
    }, 0);
  }, [privilegeMap]);

  const toggleStaffPanel = async (staffId) => {
    const id = String(staffId);
    if (expandedStaffId === id) {
      setExpandedStaffId("");
      return;
    }

    setExpandedStaffId(id);
    if (!privilegeMap[id]) {
      await loadStaffPrivileges(id);
    }
  };

  const togglePrivilege = async (staffId, moduleId, currentStatus) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !staffId || !moduleId) return;

    const nextStatus = currentStatus === "Granted" ? "InActive" : "Active";
    const updateKey = `${staffId}|${moduleId}`;

    setUpdatingKey(updateKey);
    try {
      const res = await axios.get(`${API_BASE()}updateStaffPrivilege.php`, {
        params: {
          school_code: schoolCode,
          staff_id: staffId,
          module_id: moduleId,
          status: nextStatus,
        },
      });

      if (res.data?.api_status === "OK") {
        setPrivilegeMap((prev) => {
          const rows = Array.isArray(prev[String(staffId)]) ? prev[String(staffId)] : [];
          return {
            ...prev,
            [String(staffId)]: rows.map((row) =>
              String(row.module_id) === String(moduleId)
                ? {
                    ...row,
                    status: nextStatus === "Active" ? "Granted" : "Denied",
                  }
                : row
            ),
          };
        });
        myToast.notify_success("Transport permission updated.");
      } else {
        myToast.notify_warning(
          res.data?.api_remark || "Could not update transport permission."
        );
      }
    } catch (error) {
      console.error("[TransportPermission] update error", error);
      myToast.notify_warning("Network error updating permission.");
    } finally {
      setUpdatingKey("");
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div
      className="content payroll-page tp-page tp-perm-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div className="content-header" style={{ background: COLORS.primary_color_shade5 }}>
        <div className="leftside-content-header d-flex align-items-center">
          <BackButton />
          <ul className="breadcrumbs mb-0">
            <li>
              <Link to="/AdminMain/Transport" style={{ color: COLORS.white }}>
                Transport
              </Link>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Permissions</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp tp-shell">
        <header className="tp-hero tp-perm-hero" style={{ "--tp-hero-bg": HERO_GRADIENT }}>
          <div className="tp-hero__brand">
            <div className="tp-hero__icon" aria-hidden>
              <FaShieldAlt />
            </div>
            <div>
              <h1 className="tp-hero__title">Transport Permissions</h1>
              <p className="tp-hero__sub">
                {staffList.length} staff · {grantedCount} granted transport module
                {grantedCount === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="tp-hero__tools">
            <div className="tp-hero__search-group">
              <input
                type="text"
                className="tp-hero__search"
                placeholder="Search staff name, role, ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search staff"
              />
              <button
                type="button"
                className={`pp-icon-btn ${loading ? "spinning" : ""}`}
                onClick={loadStaff}
                disabled={loading}
                aria-label="Refresh staff list"
              >
                <FaSyncAlt aria-hidden />
              </button>
            </div>
          </div>
        </header>

        <section className="tp-section tp-section--last" aria-label="Staff permissions">
          <div className="tp-perm-list">
            {filteredStaff.length === 0 ? (
              <div className="tp-perm-empty">
                <p className="tp-perm-empty__title">No staff found</p>
                <p className="tp-perm-empty__hint">
                  Try a different search or refresh the list.
                </p>
              </div>
            ) : (
              filteredStaff.map((staff) => {
                const staffId = staffRowKey(staff);
                if (!staffId) return null;

                const isExpanded = expandedStaffId === staffId;
                const modules = privilegeMap[staffId] || [];
                const isPrivilegeLoading = privilegeLoadingId === staffId;
                const grantedModules = modules.filter((row) => row.status === "Granted");

                return (
                  <article key={staffId} className="tp-perm-card">
                    <div className="tp-perm-card__head">
                      <div className="tp-perm-card__profile">
                        {staff.staff_image_path ? (
                          <img
                            src={staff.staff_image_path}
                            alt=""
                            className="tp-perm-card__avatar tp-perm-card__avatar--img"
                          />
                        ) : (
                          <span className="tp-perm-card__avatar" aria-hidden>
                            {getInitials(staff.staff_name)}
                          </span>
                        )}
                        <div>
                          <h2 className="tp-perm-card__name">
                            {staff.staff_name || "Staff"}
                          </h2>
                          <p className="tp-perm-card__role">
                            {staff.staff_role || "Staff role"}
                            {staff.department_name ? ` · ${staff.department_name}` : ""}
                          </p>
                        </div>
                      </div>

                      <div className="tp-perm-card__meta">
                        <span className="tp-perm-card__badge">
                          {isExpanded && modules.length > 0
                            ? `${grantedModules.length}/${modules.length} granted`
                            : "Transport modules"}
                        </span>
                        <button
                          type="button"
                          className="tp-perm-card__toggle"
                          onClick={() => toggleStaffPanel(staffId)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <>
                              Hide
                              <FaChevronUp aria-hidden />
                            </>
                          ) : (
                            <>
                              Manage
                              <FaChevronDown aria-hidden />
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="tp-perm-card__body">
                        {isPrivilegeLoading ? (
                          <div className="tp-perm-card__loading">Loading permissions…</div>
                        ) : modules.length === 0 ? (
                          <div className="tp-perm-card__empty">
                            <FaUserTie aria-hidden />
                            <span>No transport modules found for this staff member.</span>
                          </div>
                        ) : (
                          <div className="tp-perm-grid">
                            {modules.map((module) => {
                              const isGranted = module.status === "Granted";
                              const rowKey = `${staffId}|${module.module_id}`;
                              const isUpdating = updatingKey === rowKey;

                              return (
                                <div
                                  key={module.module_id}
                                  className={`tp-perm-module${
                                    isGranted ? " tp-perm-module--granted" : ""
                                  }`}
                                >
                                  <div className="tp-perm-module__info">
                                    {module.icon_img ? (
                                      <img
                                        src={module.icon_img}
                                        alt=""
                                        className="tp-perm-module__icon"
                                      />
                                    ) : (
                                      <span className="tp-perm-module__icon-fallback" aria-hidden>
                                        <FaShieldAlt />
                                      </span>
                                    )}
                                    <div>
                                      <div className="tp-perm-module__name">
                                        {module.module_name}
                                      </div>
                                      <div
                                        className={`tp-perm-module__status tp-perm-module__status--${
                                          isGranted ? "granted" : "denied"
                                        }`}
                                      >
                                        {isGranted ? "Granted" : "Denied"}
                                      </div>
                                    </div>
                                  </div>

                                  <label className="tp-perm-switch">
                                    <input
                                      type="checkbox"
                                      checked={isGranted}
                                      disabled={isUpdating}
                                      onChange={() =>
                                        togglePrivilege(
                                          staffId,
                                          module.module_id,
                                          module.status
                                        )
                                      }
                                      aria-label={`${isGranted ? "Revoke" : "Grant"} ${
                                        module.module_name
                                      } for ${staff.staff_name || "staff"}`}
                                    />
                                    <span className="tp-perm-switch__slider" />
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </article>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TransportPermission;
